// React Query hooks for the Wave D BACS module.
//
// Endpoints come from `endpoints.bacs`. Cache invalidation:
//   - createRun / submitRun / voidRun → invalidate ['bacs'], ['receipts'],
//     ['invoices'] (per the Wave D brief — payment runs touch all three).

import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { useToast } from '../../../hooks/useToast';
import type {
  BacsRunDetailResponse,
  BacsRunListResponse,
  BacsRunStatus,
  CreateBacsRunInput,
  GenerateBacsInput,
  GenerateBacsResult,
  MarkReceiptsPaidInput,
  MarkReceiptsPaidResponse,
  UnpaidReceiptListResponse,
} from '../../../types/api';

function invalidatePaymentScope(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: queryKeys.bacs.all });
  void qc.invalidateQueries({ queryKey: queryKeys.receipts.all });
  void qc.invalidateQueries({ queryKey: queryKeys.invoices.all });
}

export interface BacsListFilters {
  page: number;
  limit?: number;
  status?: BacsRunStatus;
  date_from?: string;
  date_to?: string;
}

export function useBacsList(filters: BacsListFilters) {
  return useQuery<BacsRunListResponse>({
    queryKey: queryKeys.bacs.list(filters as unknown as Record<string, unknown>),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit));
      if (filters.status) params.set('status', filters.status);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      const res = await apiClient.get<BacsRunListResponse>(
        `${endpoints.bacs.list}?${params.toString()}`,
      );
      return res.data;
    },
    staleTime: 30_000,
  });
}

export interface UnpaidFilters {
  page: number;
  limit?: number;
}

export function useUnpaidReceipts(filters: UnpaidFilters) {
  return useQuery<UnpaidReceiptListResponse>({
    queryKey: queryKeys.bacs.unpaid(filters as unknown as Record<string, unknown>),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit));
      const res = await apiClient.get<UnpaidReceiptListResponse>(
        `${endpoints.bacs.unpaid}?${params.toString()}`,
      );
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function usePaidReceipts(filters: UnpaidFilters) {
  return useQuery<UnpaidReceiptListResponse>({
    queryKey: queryKeys.bacs.paid(filters as unknown as Record<string, unknown>),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit));
      const res = await apiClient.get<UnpaidReceiptListResponse>(
        `${endpoints.bacs.paid}?${params.toString()}`,
      );
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function useMarkReceiptsPaid() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (input: MarkReceiptsPaidInput) => {
      const res = await apiClient.post<MarkReceiptsPaidResponse>(
        endpoints.bacs.markPaid,
        input,
      );
      return res.data;
    },
    onSuccess: (data) => {
      invalidatePaymentScope(qc);
      toast.success(
        `${data.updated} receipt${data.updated === 1 ? '' : 's'} marked as paid.`,
      );
    },
    onError: () => {
      toast.error('Failed to mark receipts as paid.');
    },
  });
}

/** One-step "Generate BACS file": run + receipts paid + stored template CSV. */
export function useGenerateBacsFile() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (input: GenerateBacsInput) => {
      const res = await apiClient.post<GenerateBacsResult>(endpoints.bacs.generate, input);
      return res.data;
    },
    onSuccess: (data) => {
      invalidatePaymentScope(qc);
      toast.success(
        `BACS file generated: ${data.file.total_transactions} payment${data.file.total_transactions === 1 ? '' : 's'}.`,
      );
    },
    // 422 banking-details errors are shown inline by the page; others toast.
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      if (code !== 'BANKING_DETAILS_MISSING') toast.error('Failed to generate the BACS file.');
    },
  });
}

export function useBacsRunDetail(encId: string | undefined) {
  return useQuery<BacsRunDetailResponse>({
    queryKey: encId ? queryKeys.bacs.detail(encId) : ['bacs', 'detail', 'none'],
    queryFn: async () => {
      const res = await apiClient.get<BacsRunDetailResponse>(
        endpoints.bacs.detail(encId as string),
      );
      return res.data;
    },
    enabled: Boolean(encId),
    staleTime: 30_000,
  });
}

export function useCreateBacsRun() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: CreateBacsRunInput) =>
      apiClient.post<BacsRunDetailResponse>(endpoints.bacs.create, input),
    onSuccess: () => {
      invalidatePaymentScope(qc);
      toast.success('Payment run created.');
    },
    onError: () => {
      toast.error('Failed to create payment run.');
    },
  });
}

export function useSubmitBacsRun(encId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: () => apiClient.post<BacsRunDetailResponse>(endpoints.bacs.submit(encId)),
    onSuccess: () => {
      invalidatePaymentScope(qc);
      void qc.invalidateQueries({ queryKey: queryKeys.bacs.detail(encId) });
      toast.success('Payment run submitted. Standard 18 file is ready.');
    },
    onError: () => {
      toast.error('Failed to submit payment run.');
    },
  });
}

export function useVoidBacsRun(encId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: () => apiClient.delete(endpoints.bacs.voidRun(encId)),
    onSuccess: () => {
      invalidatePaymentScope(qc);
      void qc.invalidateQueries({ queryKey: queryKeys.bacs.detail(encId) });
      toast.success('Payment run voided.');
    },
    onError: () => {
      toast.error('Failed to void payment run.');
    },
  });
}
